/**
 * NotesSection Component
 * Displays and manages project notes with add/delete functionality
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { MessageSquare, ChevronRight, ChevronDown, Plus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { FieldSiteNotes } from './FieldSiteNotes';

// Note category options and colors
export const NOTE_CATEGORIES = ['General', 'Site', 'Client', 'Internal', 'Extras'];
export const NOTE_CATEGORY_COLORS = {
  'General': 'bg-gray-500/20 text-gray-400',
  'Site': 'bg-blue-500/20 text-blue-400',
  'Client': 'bg-purple-500/20 text-purple-400',
  'Internal': 'bg-orange-500/20 text-orange-400',
  'Extras': 'bg-green-500/20 text-green-400'
};

export function NotesSection({ 
  notes, 
  projectId, 
  api, 
  onNotesChange,
  isVisible = true 
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noteForm, setNoteForm] = useState({ 
    content: '', 
    category: 'General', 
    tags: '' 
  });

  const handleAddNote = async () => {
    if (!noteForm.content.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setSubmitting(true);
    try {
      const tagsArray = noteForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t);
      
      const response = await api.post(`/projects/${projectId}/notes`, {
        content: noteForm.content,
        category: noteForm.category,
        tags: tagsArray
      });
      
      // Notify parent of change
      if (onNotesChange) {
        onNotesChange([...notes, response.data]);
      }
      
      setNoteForm({ content: '', category: 'General', tags: '' });
      setDialogOpen(false);
      toast.success('Note added');
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/projects/${projectId}/notes/${noteId}`);
      
      // Notify parent of change
      if (onNotesChange) {
        onNotesChange(notes.filter(n => n.id !== noteId));
      }
      
      toast.success('Note deleted');
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Notes Section */}
      <div>
        <div 
          className="flex items-center justify-between mb-4 cursor-pointer"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <MessageSquare className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
              Notes ({notes.length})
            </h2>
            {collapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
          {!collapsed && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              data-testid="add-note-btn"
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          )}
        </div>

        {!collapsed && (
          <>
            {notes.length === 0 ? (
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="py-8 text-center">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No notes yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <Card key={note.id} className="bg-[#2A3240] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={`${NOTE_CATEGORY_COLORS[note.category] || NOTE_CATEGORY_COLORS['General']} text-xs`}>
                              {note.category}
                            </Badge>
                            {note.tags?.map((tag, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-white/20 text-gray-400">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-white whitespace-pre-wrap">{note.content}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {note.created_by_name} • {format(new Date(note.created_at), 'dd MMM yyyy, HH:mm')}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-gray-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
        <FieldSiteNotes projectId={projectId} api={api} />
      </div>

      {/* Add Note Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Category</Label>
              <Select value={noteForm.category} onValueChange={(v) => setNoteForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {NOTE_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Note *</Label>
              <Textarea
                value={noteForm.content}
                onChange={(e) => setNoteForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter your note..."
                rows={4}
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Tags (comma-separated)</Label>
              <Input
                value={noteForm.tags}
                onChange={(e) => setNoteForm(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="e.g., urgent, followup"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleAddNote} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
